/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Mon Aug 05 2024
 **/

import { Updater, useQueryClient } from "@tanstack/react-query";
import { FriendListMap, BlockListMap } from "./useFriendListState";

type FriendUpdateAction =
	| {
			type: "add";
			payload: { userID: string; friend: WebServices.Friend.Friends.Item };
	  }
	| {
			type: "delete";
			payload: { userID: string };
	  }
	| { type: "bookmark"; payload: { callFavorite: boolean; userID: string } }
	| { type: "block"; payload: { active: boolean; userID: string } };

type BlockUpdateAction = { blockUser: WebServices.Block.List.Item };

export default function useFriendManagerHandler() {
	const queryClient = useQueryClient();

	const setFriendList = (updater: Updater<FriendListMap | undefined, FriendListMap | undefined>) => {
		queryClient.setQueryData<FriendListMap>(["getFriendList"], updater);
	};

	const setBlockList = (updater: Updater<BlockListMap | undefined, BlockListMap | undefined>) => {
		queryClient.setQueryData<BlockListMap>(["getBlockUserList"], updater);
	};

	const updateFriend = ({ type, payload }: FriendUpdateAction) => {
		switch (type) {
			case "add": {
				setFriendList((prev) => {
					if (!prev) return prev;
					return prev.set(payload.userID, payload.friend);
				});
				break;
			}
			case "delete":
				setFriendList((prev) => {
					if (!prev) return prev;
					prev.delete(payload.userID);
					return prev;
				});
				break;
			// case "bookmark":
			// 	break;
			case "block":
				setFriendList((prev) => {
					if (!prev) return prev;
					prev.delete(payload.userID);
					return prev;
				});
				break;
			default:
				break;
		}
	};

	const updateBlock = ({ type, payload }: { type: "release" | "request"; payload: BlockUpdateAction }) => {
		switch (type) {
			case "request":
				setBlockList((prev) => {
					if (!prev) return prev;
					prev?.set(payload.blockUser.userId, payload.blockUser);
					return prev;
				});
				break;
			case "release":
				setBlockList((prev) => {
					if (!prev) return prev;
					prev?.delete(payload.blockUser.userId);
					return prev;
				});
				break;
			default:
				break;
		}
	};

	return {
		updateFriend,
		updateBlock
	};
}
